import dayjs from 'dayjs'
import { uploadImageToServer } from '@UI/Tiptap/utils/uploadImageToServer'
import { getCorrectFilePath } from '@/helpers/new/getCorrectFilePath'

const TASK_DESCRIPTION_IMAGE_BASE_WIDTH_PX = 720

/** Максимальный размер одного файла/изображения в описании задачи */
export const TASK_TRACKER_TASK_DESCRIPTION_MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024

export const TASK_TRACKER_TASK_DESCRIPTION_MAX_FILE_SIZE_LABEL = '10 МБ'

export const getTaskDescriptionFileSizeValidationError = (
  file: File,
): string | null => {
  if (file.size <= TASK_TRACKER_TASK_DESCRIPTION_MAX_FILE_SIZE_BYTES) {
    return null
  }

  const fileName = file.name?.trim() || 'Файл'

  return `${fileName}: максимальный размер файла — ${TASK_TRACKER_TASK_DESCRIPTION_MAX_FILE_SIZE_LABEL}.`
}

export const assertTaskDescriptionFileSizeAllowed = (file: File) => {
  const validationError = getTaskDescriptionFileSizeValidationError(file)
  if (validationError) {
    throw new Error(validationError)
  }
}

export const TASK_DESCRIPTION_FILE_ID_ATTR = 'data-file-id'
export const TASK_DESCRIPTION_FILE_BLOB_URL_ATTR = 'data-blob-url'

type UploadedPlatformFile = {
  id?: number | string | null
  path?: string | null
  name?: string | null
}

const getUploadedFileId = (uploaded: UploadedPlatformFile | null | undefined): number => {
  const id = Number(uploaded?.id)
  if (!Number.isFinite(id) || id <= 0) {
    throw new Error('Не удалось получить id загруженного файла')
  }

  return Math.floor(id)
}

export const extractTaskDescriptionFileIdsFromHtml = (html: string): number[] => {
  if (!html?.includes(TASK_DESCRIPTION_FILE_ID_ATTR)) return []

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const ids = new Set<number>()

  doc
    .querySelectorAll(`img[${TASK_DESCRIPTION_FILE_ID_ATTR}], a[${TASK_DESCRIPTION_FILE_ID_ATTR}]`)
    .forEach((element) => {
      const raw = element.getAttribute(TASK_DESCRIPTION_FILE_ID_ATTR)?.trim()
      const id = Number(raw)
      if (Number.isFinite(id) && id > 0) {
        ids.add(Math.floor(id))
      }
    })

  return Array.from(ids).sort((left, right) => left - right)
}

export const isEmptyDescriptionHtml = (value: string) => {
  const normalized = value.trim()

  return (
    !normalized ||
    normalized === '<p></p>' ||
    normalized === '<p><br></p>' ||
    normalized === '<p><br class="ProseMirror-trailingBreak"></p>'
  )
}

export const normalizeTaskDescriptionForApi = (html: string): string | null => {
  if (isEmptyDescriptionHtml(html)) return null

  return html.trim()
}

export const extractBlobImageUrlsFromHtml = (html: string): string[] => {
  if (!html.includes('blob:')) return []

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const urls = new Set<string>()

  doc.querySelectorAll('img[src^="blob:"]').forEach((image) => {
    const src = image.getAttribute('src')?.trim()
    if (src) urls.add(src)
  })

  return Array.from(urls)
}

const extractBlobFileLinksFromHtml = (
  html: string,
): Array<{ blobUrl: string; fileName: string }> => {
  if (
    !html.includes('blob:') &&
    !html.includes(TASK_DESCRIPTION_FILE_BLOB_URL_ATTR)
  ) {
    return []
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const map = new Map<string, string>()

  doc.querySelectorAll('a').forEach((link) => {
    if (link.getAttribute(TASK_DESCRIPTION_FILE_ID_ATTR)) return

    const blobUrl =
      link.getAttribute(TASK_DESCRIPTION_FILE_BLOB_URL_ATTR)?.trim() ||
      link.getAttribute('href')?.trim()

    if (!blobUrl?.startsWith('blob:')) return

    const nameFromAttr = link.getAttribute('data-file-name')?.trim()
    const nameFromText = link.textContent?.replace('📎', '').trim()
    const fileName = nameFromAttr || nameFromText || 'Файл'
    map.set(blobUrl, fileName)
  })

  return Array.from(map.entries()).map(([blobUrl, fileName]) => ({
    blobUrl,
    fileName,
  }))
}

const blobUrlToFile = async (blobUrl: string): Promise<File> => {
  const response = await fetch(blobUrl)
  const blob = await response.blob()
  const extension = blob.type.split('/')[1]?.split('+')[0] || 'png'

  return new File([blob], `image.${extension}`, {
    type: blob.type || 'image/png',
  })
}

const normalizeImageWidth = (width: string | null | undefined) => {
  const raw = width?.trim()
  if (!raw) return '100%'

  if (raw.endsWith('%') || raw.endsWith('px')) return raw

  return `${raw}%`
}

export const resolveImageWidthForSave = (
  width: string | null | undefined,
  containerWidth: number,
): string => {
  const effectiveContainerWidth = Math.max(
    1,
    Math.max(containerWidth, TASK_DESCRIPTION_IMAGE_BASE_WIDTH_PX),
  )
  const raw = normalizeImageWidth(width)

  if (raw.endsWith('px')) return raw

  if (raw.endsWith('%')) {
    const percent = Number.parseFloat(raw)
    if (!Number.isFinite(percent)) {
      return `${effectiveContainerWidth}px`
    }

    return `${Math.max(1, Math.round((percent / 100) * effectiveContainerWidth))}px`
  }

  const numeric = Number.parseFloat(raw)
  if (!Number.isFinite(numeric)) {
    return `${effectiveContainerWidth}px`
  }

  if (numeric <= 100) {
    return `${Math.max(1, Math.round((numeric / 100) * effectiveContainerWidth))}px`
  }

  return `${Math.max(1, Math.round(numeric))}px`
}

const applyImageWidthAttributes = (image: HTMLImageElement, width: string) => {
  const normalizedWidth = width.endsWith('px')
    ? width
    : resolveImageWidthForSave(width, image.ownerDocument?.documentElement?.clientWidth || 800)

  image.setAttribute('style', `width: ${normalizedWidth}; height: auto;`)
}

export const enrichDescriptionHtmlWithImageWidths = (
  html: string,
  imageWidths: string[],
  containerWidth = 0,
): string => {
  if (!html.includes('<img') || !imageWidths.length) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const images = Array.from(doc.querySelectorAll('img'))

  images.forEach((image, index) => {
    const width = imageWidths[index]
    if (!width) return

    const widthForSave = width.endsWith('px')
      ? width
      : resolveImageWidthForSave(width, containerWidth || 800)

    applyImageWidthAttributes(image, widthForSave)
  })

  return doc.body.innerHTML
}

export const normalizeDescriptionImagesInHtml = (html: string): string => {
  if (!html.includes('<img')) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')

  doc.querySelectorAll('img').forEach((image) => {
    const width =
      image.style.width ||
      image.getAttribute('data-width') ||
      image.getAttribute('width') ||
      '100%'

    applyImageWidthAttributes(image, width)
  })

  return doc.body.innerHTML
}

const replaceBlobUrlsInHtml = (
  html: string,
  replacements: Record<string, { url: string; fileId: number }>,
): string => {
  if (!Object.keys(replacements).length) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')

  doc.querySelectorAll('img[src^="blob:"]').forEach((image) => {
    const src = image.getAttribute('src')?.trim()
    if (!src || !replacements[src]) return

    const next = replacements[src]
    image.setAttribute('src', next.url)
    image.setAttribute(TASK_DESCRIPTION_FILE_ID_ATTR, String(next.fileId))
  })

  return doc.body.innerHTML
}

const replaceBlobFileLinksInHtml = (
  html: string,
  replacements: Record<string, { url: string; fileName: string; fileId: number }>,
): string => {
  if (!Object.keys(replacements).length) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')

  doc.querySelectorAll('a').forEach((link) => {
    const href = link.getAttribute('href')?.trim()
    const blobUrl =
      link.getAttribute(TASK_DESCRIPTION_FILE_BLOB_URL_ATTR)?.trim() || href

    if (!blobUrl?.startsWith('blob:')) return

    const next = replacements[blobUrl]
    if (!next) return

    link.setAttribute('href', next.url)
    link.setAttribute(TASK_DESCRIPTION_FILE_ID_ATTR, String(next.fileId))
    link.setAttribute('data-file-name', next.fileName)
    link.removeAttribute(TASK_DESCRIPTION_FILE_BLOB_URL_ATTR)
    link.textContent = `📎 ${next.fileName}`
  })

  return doc.body.innerHTML
}

const replacePendingFileMarkersInHtml = (
  html: string,
  replacements: Array<{ fileName: string; url: string; fileId: number }>,
): string => {
  if (!replacements.length) return html

  const doc = new DOMParser().parseFromString(html, 'text/html')

  const findTextNodeByMarker = (marker: string) => {
    const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT)
    let current = walker.nextNode()

    while (current) {
      const textNode = current as Text
      const raw = textNode.textContent?.trim() ?? ''
      const parent = textNode.parentElement
      if (raw === marker && parent && parent.tagName !== 'A') {
        return textNode
      }
      current = walker.nextNode()
    }

    return null
  }

  replacements.forEach(({ fileName, url, fileId }) => {
    const marker = `📎 ${fileName}`

    const existingAnchor = Array.from(doc.querySelectorAll('a')).find((anchor) => {
      const text = anchor.textContent?.trim() ?? ''
      if (text !== marker) return false

      const href = anchor.getAttribute('href')?.trim() ?? ''
      return !href || href === '#' || href.startsWith('blob:')
    })
    if (existingAnchor) {
      existingAnchor.setAttribute('href', url)
      existingAnchor.setAttribute('target', '_blank')
      existingAnchor.setAttribute('rel', 'noopener noreferrer')
      existingAnchor.setAttribute(TASK_DESCRIPTION_FILE_ID_ATTR, String(fileId))
      existingAnchor.setAttribute('data-file-name', fileName)
      return
    }

    const textNode = findTextNodeByMarker(marker)
    if (!textNode) return

    const anchor = doc.createElement('a')
    anchor.setAttribute('href', url)
    anchor.setAttribute('target', '_blank')
    anchor.setAttribute('rel', 'noopener noreferrer')
    anchor.setAttribute(TASK_DESCRIPTION_FILE_ID_ATTR, String(fileId))
    anchor.setAttribute('data-file-name', fileName)
    anchor.textContent = marker

    textNode.parentNode?.replaceChild(anchor, textNode)
  })

  return doc.body.innerHTML
}

const buildTaskDescriptionUploadFileName = (
  taskId: number | null | undefined,
  kind: 'image' | 'file',
  fileName?: string,
  suffix = '',
) => {
  const taskSuffix = taskId ? ` #${taskId}` : ''
  if (kind === 'file') {
    const normalizedName = fileName?.trim() || 'Файл'
    return `файл из описания задачи${taskSuffix} — ${normalizedName}`
  }

  return `изображение из описания задачи${taskSuffix} ${dayjs().format('MM.DD.YYYY HH:mm:ss')}${suffix}`
}

export const prepareTaskDescriptionHtmlForSave = async (
  html: string,
  profileId: number | null | undefined,
  imageWidths: string[] = [],
  containerWidth = 0,
  pendingAttachmentFiles: Array<{
    blobUrl: string
    file: File
    fileName: string
  }> = [],
  taskId?: number | null,
): Promise<string> => {
  const htmlWithWidths = enrichDescriptionHtmlWithImageWidths(
    html,
    imageWidths,
    containerWidth,
  )
  const blobUrls = extractBlobImageUrlsFromHtml(htmlWithWidths)
  const blobFileLinks = extractBlobFileLinksFromHtml(htmlWithWidths)
  const blobFileLinkUrlSet = new Set(blobFileLinks.map((item) => item.blobUrl))
  const pendingFilesToUpload = pendingAttachmentFiles.filter((item) =>
    blobFileLinkUrlSet.has(item.blobUrl),
  )

  if (!blobUrls.length && !blobFileLinks.length && !pendingFilesToUpload.length) {
    return normalizeDescriptionImagesInHtml(htmlWithWidths)
  }

  if (!profileId) {
    throw new Error('Не удалось определить пользователя для загрузки вложений')
  }

  const replacements: Record<string, { url: string; fileId: number }> = {}
  const fileReplacements: Record<
    string,
    { url: string; fileName: string; fileId: number }
  > = {}
  const textMarkerReplacements: Array<{
    fileName: string
    url: string
    fileId: number
  }> = []
  const pendingFilesMap = new Map(
    pendingFilesToUpload.map((item) => [item.blobUrl, item]),
  )
  const usedPendingBlobUrls = new Set<string>()

  await Promise.all(
    blobUrls.map(async (blobUrl, index) => {
      const file = await blobUrlToFile(blobUrl)
      assertTaskDescriptionFileSizeAllowed(file)
      const suffix = blobUrls.length > 1 ? ` (${index + 1})` : ''
      const fileName = buildTaskDescriptionUploadFileName(taskId, 'image', undefined, suffix)
      const uploaded = await uploadImageToServer({
        file,
        fileName,
        profileId,
      })

      const path = uploaded?.path
      if (!path) {
        throw new Error('Не удалось загрузить изображение')
      }

      replacements[blobUrl] = {
        url: getCorrectFilePath(path),
        fileId: getUploadedFileId(uploaded),
      }
    }),
  )

  await Promise.all(
    blobFileLinks.map(async ({ blobUrl, fileName }) => {
      const pending = pendingFilesMap.get(blobUrl)
      if (pending) {
        usedPendingBlobUrls.add(blobUrl)
      }
      const file = pending?.file ?? (await blobUrlToFile(blobUrl))
      assertTaskDescriptionFileSizeAllowed(file)
      const nextFileName = buildTaskDescriptionUploadFileName(
        taskId,
        'file',
        pending?.fileName || fileName,
      )
      const uploaded = await uploadImageToServer({
        file,
        fileName: nextFileName,
        profileId,
      })
      const path = uploaded?.path
      if (!path) {
        throw new Error('Не удалось загрузить файл')
      }

      fileReplacements[blobUrl] = {
        url: getCorrectFilePath(path),
        fileName: nextFileName,
        fileId: getUploadedFileId(uploaded),
      }
    }),
  )

  await Promise.all(
    pendingFilesToUpload
      .filter((item) => !usedPendingBlobUrls.has(item.blobUrl))
      .map(async (item) => {
        assertTaskDescriptionFileSizeAllowed(item.file)
        const uploaded = await uploadImageToServer({
          file: item.file,
          fileName: buildTaskDescriptionUploadFileName(taskId, 'file', item.fileName),
          profileId,
        })
        const path = uploaded?.path
        if (!path) {
          throw new Error('Не удалось загрузить файл')
        }

        const url = getCorrectFilePath(path)
        fileReplacements[item.blobUrl] = {
          url,
          fileName: item.fileName,
          fileId: getUploadedFileId(uploaded),
        }
        textMarkerReplacements.push({
          fileName: item.fileName,
          url,
          fileId: getUploadedFileId(uploaded),
        })
      }),
  )

  const htmlWithServerUrls = replaceBlobUrlsInHtml(htmlWithWidths, replacements)
  const htmlWithUploadedFiles = replaceBlobFileLinksInHtml(
    htmlWithServerUrls,
    fileReplacements,
  )
  const htmlWithUploadedFilesAndMarkers = replacePendingFileMarkersInHtml(
    htmlWithUploadedFiles,
    textMarkerReplacements,
  )

  return normalizeDescriptionImagesInHtml(htmlWithUploadedFilesAndMarkers)
}
